/**
 * Account authentication business logic.
 *
 * Pure validation functions are imported from @core/utils/tier.
 * This module contains async orchestrators that compose
 * API / storage / crypto modules (browser-dependent).
 */

import {
  getStoredApiKey,
  setStoredApiKey,
  clearStoredApiKey,
  getSharePasscode,
  storeSharePasscode,
} from '@/lib/storage/local-storage'
import {
  generateApiKey,
  validateApiKey,
  ApiError,
  type ApiKeyResponse,
  type ApiKeyStats,
  type ShareSummary,
} from '@/lib/api/keys'
import { decryptPasscodeWithApiKey } from '@/lib/crypto/aes-gcm'
import { validateAccountName, validateApiKeyFormat } from '@core/utils/tier'

// Re-export pure validation functions from core for backward compatibility
export {
  validateAccountName,
  validateApiKeyFormat,
  maskSecretKey,
} from '@core/utils/tier'
export {
  getShareDisplayName,
  isShareEncrypted,
} from '@core/utils/share'

// ---------------------------------------------------------------------------
// Async orchestrators (browser-dependent)
// ---------------------------------------------------------------------------

/**
 * Create a new account with the given name.
 * Returns the generated API key data. Does NOT store the key or dispatch events.
 */
export async function signUpAccount(
  name: string,
): Promise<ApiKeyResponse> {
  const validation = validateAccountName(name)
  if (!validation.valid) {
    throw new Error(validation.error)
  }
  try {
    return await generateApiKey({ name: name.trim() })
  } catch (err) {
    throw err instanceof ApiError ? err : new Error('Failed to create account')
  }
}

/**
 * Sign in by validating an existing API key.
 * Returns stats on success or a descriptive error on failure.
 * Does NOT store the key or dispatch events.
 */
export async function signInWithApiKey(
  key: string,
): Promise<{ valid: true; stats: ApiKeyStats } | { valid: false; error: string }> {
  const formatCheck = validateApiKeyFormat(key)
  if (!formatCheck.valid) {
    return { valid: false, error: formatCheck.error }
  }

  try {
    const stats = await validateApiKey(key.trim())
    if (!stats.valid) {
      if (stats.reason === 'revoked') {
        return { valid: false, error: 'This secret key has been revoked' }
      }
      if (stats.reason === 'expired') {
        return { valid: false, error: 'This secret key has expired' }
      }
      return { valid: false, error: 'Invalid secret key' }
    }
    return { valid: true, stats }
  } catch (err) {
    if (err instanceof ApiError) {
      return { valid: false, error: err.message }
    }
    return { valid: false, error: 'Failed to validate key. Please try again.' }
  }
}

/**
 * For each share that has a `wrappedPasscode` and no locally stored passcode,
 * decrypt the passcode with the API key and persist it to localStorage.
 * Returns a map of shareId → decrypted passcode for newly decrypted entries.
 */
export async function batchDecryptPasscodes(
  shares: ShareSummary[],
  apiKey: string,
): Promise<Record<string, string>> {
  const results: Record<string, string> = {}

  for (const share of shares) {
    if (getSharePasscode(share.id)) continue
    if (!share.wrappedPasscode) continue

    try {
      const passcode = await decryptPasscodeWithApiKey(share.wrappedPasscode, apiKey)
      results[share.id] = passcode
      storeSharePasscode(share.id, passcode)
    } catch (error) {
      console.warn(`Failed to decrypt passcode for share ${share.id}:`, error)
    }
  }

  return results
}

// ---------------------------------------------------------------------------
// Side-effect helpers (browser-dependent)
// ---------------------------------------------------------------------------

/**
 * Store an API key in localStorage and fire the global change event.
 */
export function commitApiKey(apiKey: string): void {
  setStoredApiKey(apiKey)
  window.dispatchEvent(new Event('apikey-changed'))
}

/**
 * Remove the stored API key and fire the global change event.
 */
export function clearApiKeyAndNotify(): void {
  clearStoredApiKey()
  window.dispatchEvent(new Event('apikey-changed'))
}

/**
 * Convenience: get the currently stored API key (re-export for single import).
 */
export { getStoredApiKey }
